<template>
    <v-dialog
        :value="value"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
        @input="$emit('input', $event)"
    >
        <v-card class="strategy-root" tile>
            <v-app-bar flat color="transparent" class="strategy-appbar" height="72">
                <v-btn icon :disabled="submitting" @click="close"><v-icon>mdi-close</v-icon></v-btn>

                <div class="ml-2 strategy-theme-box">
                    <div class="mercury--text text-caption">Tema selecionado</div>
                    <div class="stardust--text font-weight-medium one-line-text">{{ edited.theme }}</div>
                </div>

                <v-spacer />

                <v-btn
                    color="primary"
                    large
                    :disabled="!canSubmit || submitting"
                    :loading="submitting"
                    @click="submit"
                >
                    {{ submitting ? 'Preparando...' : 'Criar meu artigo' }}
                    <v-icon v-if="!submitting" right>mdi-arrow-right</v-icon>
                </v-btn>
            </v-app-bar>

            <v-container v-if="!advanced" class="strategy-container py-10">
                <!-- MODO SIMPLES: resumo pré-selecionado, sem decisões obrigatórias -->
                <empty-state
                    v-if="!organic.length"
                    icon="mdi-magnify-close"
                    title="Não encontramos boas referências para este tema."
                >
                    <v-btn color="primary" class="mt-4" @click="close">Escolher outro tema</v-btn>
                </empty-state>

                <template v-else>
                    <transition name="fade-slide">
                        <div v-if="show.intro" class="strategy-intro mb-8">
                            <div class="strategy-intro-icon mb-4">
                                <v-icon color="primary" size="28">mdi-check-decagram-outline</v-icon>
                            </div>
                            <h1 class="text-h4 stardust--text font-weight-semibold mb-2">Está quase pronto</h1>
                            <p class="mercury--text text-body-1 ma-0" style="max-width: 640px;">
                                Preparamos tudo para você. Confira o resumo abaixo e confirme — nós cuidamos do resto.
                            </p>
                        </div>
                    </transition>

                    <div v-if="show.intro" class="summary-panel mb-6">
                        <div class="summary-row">
                            <div class="summary-label mercury--text">Tema do artigo</div>
                            <div class="summary-value">
                                <p class="stardust--text text-subtitle-1 font-weight-medium ma-0">{{ edited.theme }}</p>
                                <p v-if="edited.keyword" class="mercury--text text-body-2 ma-0 mt-1">Palavra-chave: {{ edited.keyword }}</p>
                            </div>
                        </div>
                        <div class="summary-row">
                            <div class="summary-label mercury--text">Referências que vamos analisar</div>
                            <div class="summary-value">
                                <div v-for="(item, i) in summaryOrganic" :key="`sum-org-${i}`" class="summary-ref">
                                    <span class="position-chip">#{{ item.position }}</span>
                                    <span class="summary-ref__title stardust--text">{{ item.title }}</span>
                                    <span class="summary-ref__domain mercury--text">{{ prettyLink(item.link) }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="summaryTopics.length" class="summary-row">
                            <div class="summary-label mercury--text">Assuntos que as pessoas buscam</div>
                            <div class="summary-value summary-chips">
                                <span v-for="(topic, i) in summaryTopics" :key="`sum-topic-${i}`" class="summary-chip">
                                    <v-icon size="13" color="primary" class="mr-1">{{ topic.source === 'paa' ? 'mdi-help-circle-outline' : 'mdi-magnify' }}</v-icon>
                                    {{ topic.label }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <info-notice
                        v-if="show.intro"
                        variant="primary"
                        icon="mdi-auto-fix"
                        description="Escolhemos automaticamente as melhores referências do Google para este tema. Quer mudar algo?"
                        class="mb-8"
                    >
                        <template #actions>
                            <v-btn small text color="primary" class="adjust-btn" :disabled="submitting" @click="advanced = true">
                                Ajustar detalhes
                            </v-btn>
                        </template>
                    </info-notice>

                    <div v-if="show.intro" class="simple-cta">
                        <v-btn
                            color="primary"
                            x-large
                            :disabled="!canSubmit || submitting"
                            :loading="submitting"
                            @click="submit"
                        >
                            Criar meu artigo
                            <v-icon right>mdi-arrow-right</v-icon>
                        </v-btn>
                        <p class="mercury--text text-body-2 ma-0 mt-3">A escrita leva alguns minutos. Você revisa antes de publicar.</p>
                    </div>
                </template>
            </v-container>

            <v-container v-else class="strategy-container py-10">
                <transition name="fade-slide">
                    <div v-if="show.intro" class="strategy-intro mb-10">
                        <div class="strategy-intro-icon mb-4">
                            <v-icon color="primary" size="28">mdi-chart-timeline-variant-shimmer</v-icon>
                        </div>
                        <h1 class="text-h4 stardust--text font-weight-semibold mb-2">Ajuste a estratégia do seu artigo</h1>
                        <p class="mercury--text text-body-1 ma-0" style="max-width: 640px;">
                            Escolha quais referências e tópicos devem guiar o conteúdo. Nossa IA vai usar isso para produzir um artigo com maior chance de aparecer no Google.
                        </p>
                    </div>
                </transition>

                <!-- SUGGESTION (editable) -->
                <section class="mb-12">
                    <transition name="fade-slide">
                        <header v-if="show.intro" class="strategy-section-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <div class="section-index">00</div>
                                <h2 class="text-h5 stardust--text font-weight-semibold ma-0">Sua sugestão</h2>
                            </div>
                            <p class="mercury--text text-body-2 ma-0">
                                Esses campos serão usados para gerar e otimizar o artigo. Clique no lápis se quiser ajustar algum.
                            </p>
                        </header>
                    </transition>

                    <div v-if="show.intro" class="suggestion-edit">
                        <div v-for="field in fields" :key="field.key" class="suggestion-row" :class="{ 'is-editing': editing[field.key] }">
                            <div class="suggestion-label mercury--text">{{ field.label }}</div>
                            <div class="suggestion-value">
                                <template v-if="editing[field.key]">
                                    <v-textarea
                                        v-if="field.multiline"
                                        v-model="edited[field.key]"
                                        outlined
                                        dense
                                        hide-details="auto"
                                        auto-grow
                                        rows="2"
                                        :counter="field.counter"
                                        :placeholder="field.placeholder"
                                        :hint="field.hint"
                                        persistent-hint
                                        autofocus
                                        @blur="commitEdit(field.key)"
                                    />
                                    <v-text-field
                                        v-else
                                        v-model="edited[field.key]"
                                        outlined
                                        dense
                                        hide-details="auto"
                                        :counter="field.counter"
                                        :placeholder="field.placeholder"
                                        :hint="field.hint"
                                        persistent-hint
                                        autofocus
                                        @blur="commitEdit(field.key)"
                                        @keydown.enter.prevent="commitEdit(field.key)"
                                    />
                                </template>
                                <template v-else>
                                    <p v-if="edited[field.key]" class="suggestion-text stardust--text ma-0" :class="field.textClass">{{ edited[field.key] }}</p>
                                    <p v-else class="suggestion-text suggestion-text--empty mercury--text ma-0">{{ field.placeholder }}</p>
                                </template>
                            </div>
                            <div class="suggestion-action">
                                <v-btn icon small :color="editing[field.key] ? 'primary' : 'mercury'" :disabled="submitting" @click="toggleEdit(field.key)">
                                    <v-icon small>{{ editing[field.key] ? 'mdi-check' : 'mdi-pencil-outline' }}</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- RELATED TOPICS -->
                <section v-if="relatedTopics.length" class="mb-12">
                    <transition name="fade-slide">
                        <header v-if="show.topics" class="strategy-section-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <div class="section-index">01</div>
                                <h2 class="text-h5 stardust--text font-weight-semibold ma-0">Quer incluir termos populares do Google?</h2>
                            </div>
                            <p class="mercury--text text-body-2 ma-0">
                                Encontramos assuntos muito buscados relacionados ao tema do artigo. A seleção é opcional, mas pode ajudar a IA a criar um conteúdo mais alinhado com o que as pessoas procuram.
                                <span class="counter-pill" :class="{ 'is-active': selectedTopics.length > 0 }">
                                    {{ selectedTopics.length }}/5
                                </span>
                            </p>
                        </header>
                    </transition>

                    <div v-if="show.topics" class="topics-grid">
                        <button
                            v-for="(topic, i) in relatedTopics"
                            :key="`topic-${i}`"
                            type="button"
                            class="topic-chip"
                            :class="{
                                'is-selected': isTopicSelected(i),
                                'is-disabled': !isTopicSelected(i) && selectedTopics.length >= 5,
                                'is-question': topic.source === 'paa',
                            }"
                            :style="{ '--stagger': `${i * 50}ms` }"
                            @click="toggleTopic(i)"
                        >
                            <v-icon
                                small
                                :color="isTopicSelected(i) ? 'primary' : 'mercury'"
                                class="mr-2"
                            >
                                {{ isTopicSelected(i) ? 'mdi-check-circle' : (topic.source === 'paa' ? 'mdi-help-circle-outline' : 'mdi-magnify') }}
                            </v-icon>
                            <span class="topic-label">{{ topic.label }}</span>
                        </button>
                    </div>
                </section>

                <!-- ORGANIC -->
                <section v-if="organic.length" class="mb-12">
                    <transition name="fade-slide">
                        <header v-if="show.organic" class="strategy-section-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <div class="section-index">02</div>
                                <h2 class="text-h5 stardust--text font-weight-semibold ma-0">
                                    <span class="crown" aria-hidden="true">👑</span>
                                    Top resultados no Google
                                </h2>
                            </div>
                            <p class="mercury--text text-body-2 ma-0">
                                Estas páginas estão muito bem rankeadas pelo Google. Selecione até 3 para analisar e repetir suas estratégias vencedoras.
                                <span class="counter-pill" :class="{ 'is-active': selectedOrganic.length > 0 }">
                                    {{ selectedOrganic.length }}/3
                                </span>
                            </p>
                        </header>
                    </transition>

                    <div v-if="show.organic" class="strategy-list">
                        <div
                            v-for="(item, i) in organic"
                            :key="`org-${i}`"
                            class="strategy-item"
                            :class="{
                                'is-selected': isOrganicSelected(i),
                                'is-disabled': !isOrganicSelected(i) && selectedOrganic.length >= 3,
                            }"
                            :style="{ '--stagger': `${i * 520}ms` }"
                            @click="toggleOrganic(i)"
                        >
                            <div class="strategy-item-thumb">
                                <img
                                    v-if="previews[i] && previews[i].image"
                                    :src="previews[i].image"
                                    :alt="item.title"
                                    class="strategy-item-thumb-img"
                                    @error="onImageError(i)"
                                />
                                <div v-else-if="previews[i] && previews[i].loaded" class="strategy-item-thumb-empty">
                                    <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                                </div>
                                <div v-else class="strategy-item-thumb-skeleton"></div>
                            </div>

                            <div class="strategy-item-check">
                                <v-icon :color="isOrganicSelected(i) ? 'primary' : 'grey lighten-1'" size="24">
                                    {{ isOrganicSelected(i) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}
                                </v-icon>
                            </div>

                            <div class="strategy-item-body">
                                <div class="d-flex align-center mb-2">
                                    <span class="position-chip">#{{ item.position }}</span>
                                    <span v-if="item.date" class="mercury--text text-caption ml-2">{{ item.date }}</span>
                                </div>
                                <h3 class="stardust--text text-subtitle-1 font-weight-medium mb-1">{{ item.title }}</h3>
                                <p class="mercury--text text-body-2 mb-2">{{ item.snippet }}</p>
                                <a
                                    :href="item.link"
                                    target="_blank"
                                    rel="noopener"
                                    class="strategy-link primary--text"
                                    @click.stop
                                >
                                    <v-icon small color="primary" class="mr-1">mdi-open-in-new</v-icon>
                                    <span>{{ prettyLink(item.link) }}</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </v-container>
        </v-card>
    </v-dialog>
</template>

<script>
import Api from '@/lib/Api'

export default {
    name: 'ArtigosStrategyDialog',
    props: {
        value: { type: Boolean, default: false },
        suggestion: { type: Object, default: () => ({}) },
        data: { type: Object, default: () => ({}) },
        submitting: { type: Boolean, default: false },
    },
    data() {
        return {
            advanced: false,
            selectedOrganic: [],
            selectedTopics: [],
            show: { intro: false, organic: false, topics: false },
            timers: [],
            previews: {},
            edited: { theme: '', title: '', keyword: '', meta_description: '' },
            editing: { theme: false, title: false, keyword: false, meta_description: false },
            fields: [
                { key: 'theme', label: 'Tema', multiline: false, placeholder: 'Defina o tema do artigo', hint: '', counter: null, textClass: 'text-subtitle-1 font-weight-medium' },
                { key: 'keyword', label: 'Palavra-chave', multiline: false, placeholder: 'Ex.: tratamento de varizes', hint: '3 a 60 caracteres', counter: 60, textClass: 'text-body-2' },
                { key: 'title', label: 'Título sugerido', multiline: false, placeholder: 'Sem título sugerido — clique no lápis para definir.', hint: '50 a 60 caracteres', counter: 60, textClass: 'text-subtitle-1' },
                { key: 'meta_description', label: 'Meta descrição', multiline: true, placeholder: 'Sem meta description sugerida — clique no lápis para definir.', hint: '150 a 160 caracteres', counter: 160, textClass: 'text-body-2' },
            ],
        }
    },
    computed: {
        organic() {
            return (this.data && this.data.organic) || []
        },
        relatedTopics() {
            const topics = []
            const paa = (this.data && this.data.peopleAlsoAsk) || []
            const related = (this.data && this.data.relatedSearches) || []
            for (const p of paa) {
                if (p && p.question) topics.push({ label: p.question, source: 'paa' })
            }
            for (const r of related) {
                if (r && r.query) topics.push({ label: r.query, source: 'related' })
            }
            return topics
        },
        canSubmit() {
            return this.selectedOrganic.length >= 1 && !!(this.edited.theme && this.edited.theme.trim())
        },
        summaryOrganic() {
            return this.selectedOrganic.map(i => this.organic[i]).filter(Boolean)
        },
        summaryTopics() {
            return this.selectedTopics.map(i => this.relatedTopics[i]).filter(Boolean)
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(v) {
                this.clearTimers()
                if (!v) {
                    this.show = { intro: false, organic: false, topics: false }
                    this.previews = {}
                    this.editing = { theme: false, title: false, keyword: false, meta_description: false }
                    return
                }
                this.advanced = false
                this.previews = {}
                this.show = { intro: false, organic: false, topics: false }
                this.editing = { theme: false, title: false, keyword: false, meta_description: false }
                this.edited = {
                    theme: this.suggestion?.theme || '',
                    title: this.suggestion?.title || '',
                    keyword: this.suggestion?.keyword || '',
                    meta_description: this.suggestion?.meta_description || '',
                }
                // Pré-seleção automática (modo simples), espelhando o fluxo
                // automático do calendário: 2 primeiros resultados orgânicos +
                // até 3 perguntas frequentes, completando com buscas
                // relacionadas até 5 termos. "Ajustar detalhes" abre o modo
                // avançado com essas seleções já aplicadas.
                this.selectedOrganic = this.organic.slice(0, 2).map((_, i) => i)
                const paaIdx = []
                const relIdx = []
                this.relatedTopics.forEach((t, i) => (t.source === 'paa' ? paaIdx : relIdx).push(i))
                this.selectedTopics = [...paaIdx.slice(0, 3), ...relIdx].slice(0, 5)
                this.timers.push(setTimeout(() => { this.show.intro = true }, 120))
                this.timers.push(setTimeout(() => { this.show.topics = true }, 520))
                this.timers.push(setTimeout(() => { this.show.organic = true }, 1000))
                this.fetchPreviews()
            },
        },
    },
    beforeDestroy() {
        this.clearTimers()
    },
    methods: {
        clearTimers() {
            for (const t of this.timers) clearTimeout(t)
            this.timers = []
        },
        fetchPreviews() {
            const openedAt = this.value
            this.organic.forEach(async (item, i) => {
                if (!item || !item.link) {
                    this.$set(this.previews, i, { image: null, loaded: true })
                    return
                }
                try {
                    const resp = await Api.getLinkPreview(item.link)
                    if (this.value !== openedAt) return
                    const image = (resp && !resp.error && resp.message && resp.message.image) || null
                    this.$set(this.previews, i, { image, loaded: true })
                } catch {
                    this.$set(this.previews, i, { image: null, loaded: true })
                }
            })
        },
        onImageError(i) {
            this.$set(this.previews, i, { image: null, loaded: true })
        },
        close() {
            this.$emit('input', false)
        },
        toggleOrganic(i) {
            const idx = this.selectedOrganic.indexOf(i)
            if (idx >= 0) { this.selectedOrganic.splice(idx, 1); return }
            if (this.selectedOrganic.length >= 3) return
            this.selectedOrganic.push(i)
        },
        isOrganicSelected(i) {
            return this.selectedOrganic.includes(i)
        },
        toggleTopic(i) {
            const idx = this.selectedTopics.indexOf(i)
            if (idx >= 0) { this.selectedTopics.splice(idx, 1); return }
            if (this.selectedTopics.length >= 5) return
            this.selectedTopics.push(i)
        },
        isTopicSelected(i) {
            return this.selectedTopics.includes(i)
        },
        prettyLink(link) {
            try {
                return new URL(link).hostname.replace(/^www\./, '')
            } catch {
                return link
            }
        },
        toggleEdit(key) {
            if (this.editing[key]) {
                this.commitEdit(key)
                return
            }
            this.$set(this.editing, key, true)
        },
        commitEdit(key) {
            if (!this.editing[key]) return
            if (typeof this.edited[key] === 'string') {
                this.edited[key] = this.edited[key].trim()
            }
            this.$set(this.editing, key, false)
        },
        submit() {
            if (!this.canSubmit) return
            this.$emit('submit', {
                theme: this.edited.theme,
                title: this.edited.title,
                keyword: this.edited.keyword,
                meta_description: this.edited.meta_description,
                organic: this.selectedOrganic.map(i => this.organic[i]),
                topics: this.selectedTopics.map(i => this.relatedTopics[i]),
                serpOrganic: this.organic,
                relatedSearches: (this.data && this.data.relatedSearches) || [],
            })
        },
    },
}
</script>

<style scoped>
.strategy-root {
    background-color: var(--color-background) !important;
    background-image:
        var(--bg-mesh-1),
        var(--bg-mesh-2),
        var(--bg-mesh-3),
        var(--bg-mesh-4),
        var(--bg-mesh-5),
        var(--bg-mesh-6) !important;
    min-height: 100vh;
}

.strategy-appbar {
    background: var(--glass-nav-bg) !important;
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--glass-nav-border) !important;
    position: sticky;
    top: 0;
    z-index: 4;
}

.strategy-theme-box {
    min-width: 0;
    max-width: 520px;
}

.strategy-container {
    max-width: 960px;
}

/* INTRO */
.strategy-intro-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: var(--radius-md);
    background: linear-gradient(135deg, rgba(112, 42, 225, 0.16) 0%, rgba(112, 42, 225, 0.06) 100%);
}

/* SECTION HEADER */
.strategy-section-header {
    border-left: 3px solid var(--color-primary);
    padding-left: 14px;
}

.crown {
    display: inline-block;
    font-size: 0.95em;
    margin-right: 6px;
    filter: drop-shadow(0 2px 4px rgba(234, 179, 8, 0.35));
    animation: crown-float 3.6s ease-in-out infinite;
    transform-origin: bottom center;
}

@keyframes crown-float {
    0%, 100% { transform: translateY(0) rotate(-4deg); }
    50%      { transform: translateY(-3px) rotate(4deg); }
}

.section-index {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--color-primary);
    margin-right: 10px;
    font-variant-numeric: tabular-nums;
}

.counter-pill {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 10px;
    border-radius: var(--radius-pill);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    background: var(--glass-soft-bg);
    color: var(--color-on-surface-variant);
    transition: background 200ms ease, color 200ms ease;
}

.counter-pill.is-active {
    background: var(--color-primary);
    color: var(--color-on-primary);
}

/* MODO SIMPLES (resumo) */
.summary-panel {
    background: var(--glass-panel-bg);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-md);
    padding: 8px 20px;
    box-shadow: var(--shadow-glass);
}

.summary-row {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 16px;
    padding: 18px 4px;
    border-bottom: 1px solid var(--glass-panel-border);
}

.summary-row:last-child {
    border-bottom: none;
}

.summary-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding-top: 4px;
}

.summary-value {
    min-width: 0;
}

.summary-ref {
    display: flex;
    align-items: baseline;
    gap: 10px;
    min-width: 0;
    padding: 6px 0;
}

.summary-ref__title {
    flex: 0 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.summary-ref__domain {
    flex-shrink: 0;
    font-size: 12px;
}

.summary-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.summary-chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 6px 12px;
    border-radius: var(--radius-pill);
    background: rgba(112, 42, 225, 0.07);
    border: 1px solid rgba(112, 42, 225, 0.18);
    color: var(--color-on-surface);
    font-size: 12.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.adjust-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 700;
}

.simple-cta {
    text-align: center;
    padding: 8px 0 24px;
}

@media (max-width: 600px) {
    .summary-row {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .summary-ref {
        flex-wrap: wrap;
        gap: 6px 10px;
    }

    .summary-ref__title {
        white-space: normal;
        flex-basis: 100%;
    }
}

/* SUGGESTION EDIT PANEL */
.suggestion-edit {
    background: var(--glass-panel-bg);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-md);
    padding: 4px 8px;
    box-shadow: var(--shadow-glass);
}

.suggestion-row {
    display: grid;
    grid-template-columns: 140px 1fr auto;
    gap: 16px;
    align-items: start;
    padding: 14px 12px;
    border-bottom: 1px solid var(--glass-panel-border);
    transition: background 200ms ease, box-shadow 200ms ease;
}

.suggestion-row:last-child {
    border-bottom: none;
}

.suggestion-row.is-editing {
    background: rgba(112, 42, 225, 0.04);
    border-radius: var(--radius-sm);
    box-shadow: inset 0 0 0 1px rgba(112, 42, 225, 0.25);
}

.suggestion-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding-top: 6px;
}

.suggestion-value {
    min-width: 0;
}

.suggestion-text {
    word-break: break-word;
    line-height: 1.45;
}

.suggestion-text--empty {
    font-style: italic;
    opacity: 0.85;
}

.suggestion-action {
    padding-top: 2px;
}

@media (max-width: 600px) {
    .suggestion-row {
        grid-template-columns: 1fr auto;
    }
    .suggestion-label {
        grid-column: 1 / -1;
        padding-top: 0;
    }
}

/* LIST ITEMS */
.strategy-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.strategy-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 20px;
    background: var(--glass-panel-bg);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease, background 200ms ease;
    animation: strategy-item-enter 900ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: var(--stagger, 0ms);
}

.strategy-item:hover {
    border-color: rgba(112, 42, 225, 0.4);
    box-shadow: var(--shadow-glass-hover);
}

.strategy-item.is-selected {
    border-color: var(--color-primary);
    background: linear-gradient(180deg, rgba(112, 42, 225, 0.06) 0%, var(--glass-panel-bg) 100%);
    box-shadow: 0 0 0 1px var(--color-primary), var(--shadow-glass-hover);
}

.strategy-item.is-disabled {
    opacity: 0.55;
    cursor: default;
}

.strategy-item.is-disabled:hover {
    border-color: var(--glass-panel-border);
    box-shadow: none;
}

.strategy-item.is-disabled .strategy-link {
    cursor: pointer;
}

.strategy-item-thumb {
    flex-shrink: 0;
    width: 144px;
    height: 144px;
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--glass-soft-bg);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.strategy-item-thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    animation: thumb-fade-in 420ms ease both;
}

.strategy-item-thumb-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    animation: thumb-fade-in 420ms ease both;
}

.strategy-item-thumb-skeleton {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.7) 50%, transparent 100%),
        var(--color-surface-container);
    background-size: 200% 100%, 100% 100%;
    background-repeat: no-repeat;
    animation: thumb-shimmer 1.4s ease-in-out infinite;
}

@keyframes thumb-fade-in {
    from { opacity: 0; transform: scale(0.98); }
    to   { opacity: 1; transform: scale(1); }
}

@keyframes thumb-shimmer {
    0%   { background-position: -120% 0, 0 0; }
    100% { background-position: 120% 0, 0 0; }
}

.strategy-item-check {
    flex-shrink: 0;
    padding-top: 2px;
}

.strategy-item-body {
    min-width: 0;
    flex: 1;
}

.position-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: var(--radius-xs);
    background: rgba(112, 42, 225, 0.12);
    color: var(--color-primary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.strategy-link {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.strategy-link:hover {
    text-decoration: underline;
}

/* TOPICS */
.topics-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.topic-chip {
    display: inline-flex;
    align-items: center;
    padding: 10px 16px;
    border-radius: var(--radius-pill);
    background: var(--glass-panel-bg);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    border: 1px solid var(--glass-panel-border);
    color: var(--color-on-surface);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 200ms ease, background 200ms ease, transform 160ms ease, box-shadow 200ms ease;
    text-align: left;
    max-width: 100%;
    animation: topic-chip-enter 440ms cubic-bezier(0.2, 0.7, 0.2, 1) backwards;
    animation-delay: var(--stagger, 0ms);
    font-family: inherit;
}

.topic-chip:hover {
    border-color: rgba(112, 42, 225, 0.4);
    background: rgba(112, 42, 225, 0.05);
}

.topic-chip.is-selected {
    background: rgba(112, 42, 225, 0.10);
    border-color: var(--color-primary);
    box-shadow: 0 0 0 1px var(--color-primary) inset;
    color: var(--color-primary);
}

.topic-chip.is-disabled {
    opacity: 0.45;
    pointer-events: none;
}

.topic-chip.is-question {
    font-weight: 400;
}

.topic-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 420px;
}

/* ANIMATIONS */
@keyframes strategy-item-enter {
    from {
        opacity: 0;
        transform: translateY(24px) scale(0.985);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

@keyframes topic-chip-enter {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

.fade-slide-enter-active {
    transition: opacity 500ms ease, transform 500ms ease;
}

.fade-slide-enter {
    opacity: 0;
    transform: translateY(10px);
}

@media (prefers-reduced-motion: reduce) {
    .strategy-item,
    .topic-chip,
    .crown,
    .strategy-item-thumb-img,
    .strategy-item-thumb-empty,
    .strategy-item-thumb-skeleton {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
    }
    .fade-slide-enter-active {
        transition: none !important;
    }
}

@media (max-width: 600px) {
    .strategy-theme-box { max-width: 180px; }
    .topic-label { max-width: 240px; }
    .strategy-item {
        position: relative;
        display: grid;
        grid-template-columns: 76px minmax(0, 1fr);
        gap: 12px;
        padding: 14px;
        align-items: start;
    }

    .strategy-item-thumb {
        width: 76px;
        height: 76px;
        border-radius: 10px;
    }

    .strategy-item-check {
        position: absolute;
        top: 12px;
        left: 12px;
        z-index: 1;
        padding-top: 0;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background: rgba(10, 17, 34, 0.72);
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.16);
    }

    .strategy-item-body .d-flex {
        align-items: flex-start !important;
        flex-wrap: wrap;
        gap: 4px 8px;
    }

    .strategy-item-body .text-subtitle-1 {
        font-size: 0.95rem !important;
        line-height: 1.35;
    }

    .strategy-item-body .text-body-2 {
        font-size: 0.8125rem !important;
        line-height: 1.45;
    }
}
</style>
